import { OrganizationMembershipQuery } from '@/models/user.models';
import { organizationData } from '@/store/helpers';
import { computed, Ref } from 'vue';
import intersection from 'lodash-es/intersection';

export function useOrgMembershipsQuery(options?: {
  query?: Ref<OrganizationMembershipQuery>;
}) {
  const { orgMembershipCache } = organizationData();

  const noFiltersApplied = computed(() => {
    return (
      !options?.query || !Object.values(options.query.value).some((v) => !!v)
    );
  });

  const filteredCachedMembers = computed(() => {
    if (noFiltersApplied.value) {
      return orgMembershipCache.value;
    }
    let members = orgMembershipCache.value;

    if (options?.query?.value.q) {
      members = members.filter(
        (m) =>
          m.user.name
            .toLowerCase()
            .includes(options?.query?.value.q?.toLowerCase() || '') ||
          m.user.email
            .toLowerCase()
            .includes(options?.query?.value.q?.toLowerCase() || '')
      );
    }
    if (options?.query?.value.userId?.length) {
      members = members.filter((m) =>
        options?.query?.value.userId?.includes(m.userId)
      );
    }
    if (options?.query?.value.label?.length) {
      members = members.filter((m) =>
        m.labels.some((l) => options?.query?.value.label?.includes(l))
      );
    }
    if (options?.query?.value.role?.length) {
      members = members.filter((m) =>
        options?.query?.value.role?.includes(m.role)
      );
    }
    if (options?.query?.value.status?.length) {
      members = members.filter((m) =>
        options?.query?.value.status?.includes(m.status)
      );
    }
    if (options?.query?.value.teamId?.length) {
      members = members.filter(
        (m) => intersection(m.teams, options?.query?.value.teamId).length > 0
      );
    }

    if (options?.query?.value.sort) {
      const isDesc = options?.query?.value.direction === 'desc';

      members = members.sort((a, b) => {
        if (options?.query?.value.sort === 'name') {
          const result = a.user.name
            .toLowerCase()
            .localeCompare(b.user.name.toLowerCase());
          return isDesc ? -result : result;
        }
        if (options?.query?.value.sort === 'email') {
          const result = a.user.email
            .toLowerCase()
            .localeCompare(b.user.email.toLowerCase());
          return isDesc ? -result : result;
        }
        if (options?.query?.value.sort === 'joinedDate') {
          // Handle nulls last for both ASC and DESC
          if (!a.joinedDate && !b.joinedDate) return 0;
          if (!a.joinedDate) return 1; // a is NULL, always goes last
          if (!b.joinedDate) return -1; // b is NULL, always goes last
          const result = a.joinedDate.localeCompare(b.joinedDate);
          return isDesc ? -result : result;
        }
        if (options?.query?.value.sort === 'reinstatedDate') {
          // Handle nulls last for both ASC and DESC
          if (!a.reinstatedDate && !b.reinstatedDate) return 0;
          if (!a.reinstatedDate) return 1; // a is NULL, always goes last
          if (!b.reinstatedDate) return -1; // b is NULL, always goes last
          const result = a.reinstatedDate.localeCompare(b.reinstatedDate);
          return isDesc ? -result : result;
        }
        if (options?.query?.value.sort === 'deactivationDate') {
          // Handle nulls last for both ASC and DESC
          if (!a.deactivationDate && !b.deactivationDate) return 0;
          if (!a.deactivationDate) return 1; // a is NULL, always goes last
          if (!b.deactivationDate) return -1; // b is NULL, always goes last
          const result = a.deactivationDate.localeCompare(b.deactivationDate);
          return isDesc ? -result : result;
        }
        return 0;
      });
    }

    return members;
  });

  return {
    filteredCachedMembers,
  };
}
